import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '../../store/authStore';
import { getUserId } from '../../utils/authUtils';
import { clearAllConversations } from '../../labs/shared/agent-bar/agentBarHelpers';

function currentIdentity(isAuthenticated: boolean): string | null {
  return isAuthenticated ? getUserId() : null;
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Keeps one account's data from outliving its session in this browser.
 *
 * Query keys carry no user id, so the React Query cache is shared by whoever
 * signs in next; the agent bar's saved conversations are keyed by lab and
 * studio only, and are sent back to the model as history. On sign-out or an
 * account switch, both are dropped. Signing in from signed-out refetches
 * instead, replacing the 401s a visitor collected while browsing.
 *
 * Identity is the token's `sub`, not the store's user object, which changes
 * shape once the profile loads. Must sit inside the QueryClientProvider.
 */
export function SessionDataGuard(): null {
  const queryClient = useQueryClient();
  const identityRef = useRef<string | null>(
    currentIdentity(useAuthStore.getState().isAuthenticated),
  );

  useEffect(
    () =>
      useAuthStore.subscribe(state => {
        const next = currentIdentity(state.isAuthenticated);
        const previous = identityRef.current;
        if (next === previous) return;
        identityRef.current = next;

        if (previous !== null) {
          queryClient.clear();
          clearAllConversations(safeLocalStorage());
        } else {
          void queryClient.invalidateQueries();
        }
      }),
    [queryClient],
  );

  return null;
}
